/**
 * useLocationLanguage — picks a default UI language from the user's IP geo on
 * first visit, then never runs again.
 *
 * Order of precedence (matches i18n/config.ts):
 *   1. Explicit user pick in localStorage `cr-language` → skip entirely.
 *   2. Country from a free IP geo (ipapi.co) → map via COUNTRY_TO_LANGUAGE.
 *   3. navigator.language already applied by i18next → no override.
 *
 * Fail-open: any network failure, timeout (>1.2s), or rate-limit response just
 * leaves the existing i18next-resolved language in place. This is a "polish"
 * detector, not a hard requirement — better to default to English than to
 * block render on an unreachable lookup service.
 *
 * One network call per browser per cache lifetime. Result is cached in
 * sessionStorage so SPA route changes never re-trigger it.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRY_TO_LANGUAGE } from "@/i18n/config";

const SESSION_FLAG = "cr-language-geo-tried";
const USER_PICK_KEY = "cr-language";
const LOOKUP_TIMEOUT_MS = 1200;

interface IpapiResponse {
  country_code?: string;
  country?: string;
}

async function fetchCountryCode(): Promise<string | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const resp = await fetch("https://ipapi.co/json/", {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    clearTimeout(t);
    if (!resp.ok) return null;
    const data = (await resp.json()) as IpapiResponse;
    const code = (data.country_code ?? data.country ?? "").trim().toUpperCase();
    return code.length === 2 ? code : null;
  } catch {
    clearTimeout(t);
    return null;
  }
}

export function useLocationLanguage() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Already picked? Honor the user's choice forever.
    if (window.localStorage.getItem(USER_PICK_KEY)) return;

    // Already tried this session? Don't re-fetch on route changes.
    if (window.sessionStorage.getItem(SESSION_FLAG)) return;

    // Mark immediately so a fast double-mount doesn't fire twice.
    window.sessionStorage.setItem(SESSION_FLAG, "1");

    void fetchCountryCode().then((country) => {
      if (!country) return;
      const target = COUNTRY_TO_LANGUAGE[country];
      if (!target) return;
      // Only switch if the geo target differs from what's already active —
      // if navigator.language already landed us on the right one, no-op.
      const current = (i18n.language || "").split("-")[0].toLowerCase();
      if (current === target) return;
      void i18n.changeLanguage(target);
    });
  }, [i18n]);
}
